import type { ChatMessage, PendingMessage } from '../hooks/use-chat-ws';
import { extractToolResultText } from './tool-result';

const STARTED = 'Walkthrough started: ';
const START_MESSAGE = /^Start the "(.+)" walkthrough \((walk_\w+)\)\.$/;
const END_MESSAGE = /^End the "(.+)" walkthrough \((walk_\w+)\)\.$/;
const CONTINUE_MESSAGE = /^Continue the "(.+)" walkthrough \((walk_\w+)\)\.$/;

type Walkthrough = { title: string; id: string };

export const walkthroughStartMessage = (title: string, walkthroughId: string) =>
  `Start the "${title}" walkthrough (${walkthroughId}).`;

/** The title in the landing page's start message, so the chat can show that message as an event. */
export const startedWalkthroughTitle = (content: string) => START_MESSAGE.exec(content)?.[1];

/**
 * Sent for the customer by the header's End, so it reads as an event too. It carries the id like the
 * start message, so a customer who types the same words in any chat still gets their own bubble.
 */
export const walkthroughEndMessage = ({ title, id }: Walkthrough) => `End the "${title}" walkthrough (${id}).`;

export const endedWalkthroughTitle = (content: string) => END_MESSAGE.exec(content)?.[1];

/** Sent for the customer by the landing page's Continue, so Percy gets a turn to say where things stand. */
export const walkthroughContinueMessage = (title: string, walkthroughId: string) =>
  `Continue the "${title}" walkthrough (${walkthroughId}).`;

export const continuedWalkthroughTitle = (content: string) => CONTINUE_MESSAGE.exec(content)?.[1];

const startedBy = (content: string): Walkthrough | undefined => {
  const match = START_MESSAGE.exec(content) ?? CONTINUE_MESSAGE.exec(content);
  return match ? { title: match[1], id: match[2] } : undefined;
};

const readWalkthroughs = (messages: ChatMessage[], toolResults: Record<string, unknown>) => {
  let walkthrough: Walkthrough | undefined;
  const finished: Record<string, string> = {};
  for (const message of messages) {
    if (message.type === 'user') {
      walkthrough = END_MESSAGE.test(message.content) ? undefined : (startedBy(message.content) ?? walkthrough);
      continue;
    }
    if (message.type !== 'tool_use') continue;
    if (message.tool === 'mcp__meta__complete_walkthrough') {
      if (walkthrough) finished[message.id] = walkthrough.title;
      walkthrough = undefined;
    }
    if (message.tool !== 'mcp__meta__start_walkthrough') continue;
    const result = extractToolResultText(toolResults[message.id]);
    if (result == null) continue;
    // A refused start (a live chat, a disabled walkthrough) comes back as error text, not this line.
    walkthrough = result.startsWith(STARTED)
      ? { title: result.slice(STARTED.length).split('\n')[0], id: String(message.input.walkthrough_id ?? '') }
      : undefined;
  }
  return { walkthrough, finished };
};

/**
 * The walkthrough running in this chat: Percy started one and hasn't completed it since.
 * The landing page's start message counts until Percy's start call answers, so the header is there
 * from the first moment instead of arriving seconds later; a refused start takes it away again. The
 * message sits in `pending` until the server acknowledges it, which can take a few seconds. Ending
 * it from the header takes the header away at once, before Percy wraps up.
 */
export const activeWalkthrough = (
  messages: ChatMessage[],
  toolResults: Record<string, unknown>,
  pending: PendingMessage[] = [],
) => {
  let { walkthrough } = readWalkthroughs(messages, toolResults);
  for (const message of pending) {
    walkthrough = END_MESSAGE.test(message.content) ? undefined : (startedBy(message.content) ?? walkthrough);
  }
  return walkthrough;
};

/**
 * Each complete_walkthrough call that closed a running walkthrough, by tool use id, with its title.
 * One the customer ended from the header is already gone by then, so it already has its own event.
 */
export const finishedWalkthroughs = (messages: ChatMessage[], toolResults: Record<string, unknown>) =>
  readWalkthroughs(messages, toolResults).finished;
